/**
 * SweetNight navigation — mega menu (v2)
 *
 * The header renders every dropdown inside a <template> and clones it into the
 * DOM the first time the menu opens, and the drawer does the same. So every
 * handler here is delegated from `document` — never bound to elements directly.
 *
 * Behaviour:
 *  - desktop: hover / focus / click on a sidebar category swaps the product pane
 *  - drawer:  tap a category to expand / collapse its product list
 *  - opt-in:  snowflake cursor trail on nav items flagged with [data-sn-snowflake]
 */
(function () {
  'use strict';

  var SNOWFLAKE_SVG =
    '<svg viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">' +
    '<g stroke="currentColor" stroke-width="1.15" stroke-linecap="round">' +
    '<path d="M8 2.2v11.6M3.6 4.8l8.8 6.4M3.6 11.2l8.8-6.4"/></g></svg>';

  /* ------------------------------------------------------------------ *
   * Desktop: sidebar category -> product pane
   * ------------------------------------------------------------------ */

  function activateTab(link) {
    var panel = link.closest('[data-sn-mega]');
    var tab = link.getAttribute('data-sn-tab');
    if (!panel || !tab) return;

    var links = panel.querySelectorAll('[data-sn-tab]');
    for (var i = 0; i < links.length; i += 1) {
      links[i].classList.toggle('is-active', links[i] === link);
    }

    var panes = panel.querySelectorAll('[data-sn-pane]');
    for (var j = 0; j < panes.length; j += 1) {
      panes[j].classList.toggle('is-active', panes[j].getAttribute('data-sn-pane') === tab);
    }
  }

  // `mouseover` (not `mouseenter`) so the event bubbles up to document.
  document.addEventListener('mouseover', function (event) {
    var link = event.target.closest && event.target.closest('[data-sn-tab]');
    if (link) activateTab(link);
  });

  document.addEventListener('focusin', function (event) {
    var link = event.target.closest && event.target.closest('[data-sn-tab]');
    if (link) activateTab(link);
  });

  document.addEventListener('click', function (event) {
    var link = event.target.closest && event.target.closest('[data-sn-tab]');
    if (!link) return;
    event.preventDefault();
    activateTab(link);
  });

  /* ------------------------------------------------------------------ *
   * Panel style -> the two surfaces that go white when a menu opens
   *
   * Those surfaces (`.header__container` and `.header__dropdown-backdrop`) are
   * shared by every nav item, so CSS alone cannot tell which panel is open.
   * Mirror the opening item's panel style onto #header-component; the matching
   * colour rules are emitted per block in the section CSS.
   * ------------------------------------------------------------------ */

  /*
   * Held for the length of the close fade, then dropped.
   *
   * The backdrop's colour rule is keyed on `data-sn-panel`, so removing the
   * attribute at `aria-hidden="true"` would blank the panel's surface on frame
   * one and leave only its contents to fade. The CSS fades both halves on
   * `--sn-close-fade` (180ms) — this stays just past the end of it. Keep the two
   * numbers together.
   */
  var CLEAR_DELAY = 200;
  var clearTimer = null;

  function headerRoot() {
    return document.getElementById('header-component');
  }

  function cancelPendingClear() {
    if (clearTimer === null) return;
    clearTimeout(clearTimer);
    clearTimer = null;
  }

  function applyPanelStyle(toggle) {
    var root = headerRoot();
    if (!root) return;

    var flagged = toggle ? toggle.querySelector('[data-sn-panel]') : null;

    if (flagged) {
      cancelPendingClear();
      root.setAttribute('data-sn-panel', flagged.getAttribute('data-sn-panel'));
      root.setAttribute('data-sn-panel-style', flagged.getAttribute('data-sn-panel-style') || '');
    } else {
      clearPanelStyle();
    }
  }

  function clearPanelStyle() {
    var root = headerRoot();
    if (!root) return;
    if (!root.hasAttribute('data-sn-panel')) return;

    cancelPendingClear();
    clearTimer = setTimeout(function () {
      clearTimer = null;
      var current = headerRoot();
      if (!current) return;
      current.removeAttribute('data-sn-panel');
      current.removeAttribute('data-sn-panel-style');
    }, CLEAR_DELAY);
  }

  function onToggleActivated(event) {
    var toggle = event.target.closest && event.target.closest('.header__dropdown-toggle');
    if (!toggle) return;
    applyPanelStyle(toggle);
  }

  document.addEventListener('mouseover', onToggleActivated);
  document.addEventListener('focusin', onToggleActivated);
  document.addEventListener('click', onToggleActivated);

  /**
   * The header reuses one cloned node per dropdown, so a panel would otherwise
   * reopen on whichever category was viewed last. Reset it once it closes.
   */
  function resetPanel(panel) {
    var links = panel.querySelectorAll('[data-sn-tab]');
    var panes = panel.querySelectorAll('[data-sn-pane]');
    if (!links.length) return;

    var first = links[0].getAttribute('data-sn-tab');
    for (var i = 0; i < links.length; i += 1) {
      links[i].classList.toggle('is-active', i === 0);
    }
    for (var j = 0; j < panes.length; j += 1) {
      panes[j].classList.toggle('is-active', panes[j].getAttribute('data-sn-pane') === first);
    }
  }

  if (typeof MutationObserver !== 'undefined') {
    var observer = new MutationObserver(function (records) {
      for (var i = 0; i < records.length; i += 1) {
        var target = records[i].target;
        if (!target.hasAttribute || target.getAttribute('aria-hidden') !== 'true') continue;

        var panels = target.querySelectorAll ? target.querySelectorAll('[data-sn-mega]') : [];
        for (var j = 0; j < panels.length; j += 1) {
          resetPanel(panels[j]);
        }

        // Drop the tint once the dropdown is closed, so the next item starts clean.
        if (target.id === 'HeaderDropdownMenu') {
          clearPanelStyle();
        }
      }
    });

    document.addEventListener('DOMContentLoaded', function () {
      var header = document.getElementById('header-component');
      if (!header) return;
      observer.observe(header, {
        attributes: true,
        subtree: true,
        attributeFilter: ['aria-hidden'],
      });
    });
  }

  /* ------------------------------------------------------------------ *
   * Drawer: category accordion
   * ------------------------------------------------------------------ */

  document.addEventListener('click', function (event) {
    var trigger = event.target.closest && event.target.closest('[data-sn-acc-trigger]');
    if (!trigger) return;

    var item = trigger.closest('.sn-mnav__item');
    if (!item) return;

    event.preventDefault();
    event.stopPropagation();

    var willOpen = !item.classList.contains('is-open');
    item.classList.toggle('is-open', willOpen);
    trigger.setAttribute('aria-expanded', String(willOpen));

    var icon = trigger.querySelector('.sn-mnav__icon');
    if (icon) icon.textContent = willOpen ? '−' : '+';
  });

  /* ------------------------------------------------------------------ *
   * Drawer: two-level drill-down
   *
   * Opening is the drawer's own accordion state — the inline script in
   * drawer-menu.liquid puts `.active` on `.kik_mobile_menu_item` when its
   * heading is tapped, and the mobile CSS turns that into a panel sliding in
   * from the right. So only the way *back* and the reset are needed here.
   * ------------------------------------------------------------------ */

  function closeDrawerPanel(item) {
    if (item) item.classList.remove('active');
  }

  document.addEventListener('click', function (event) {
    var back = event.target.closest && event.target.closest('[data-sn-drawer-back]');
    if (!back) return;
    event.preventDefault();
    closeDrawerPanel(back.closest('.kik_mobile_menu_item'));
  });

  /* A panel reopens on whatever it was scrolled to, so start it at the top. */
  document.addEventListener('click', function (event) {
    var heading = event.target.closest && event.target.closest('.kik_m_m_heading');
    if (!heading) return;

    var item = heading.closest('.kik_mobile_menu_item');
    var body = item && item.querySelector('.sn-drawer__panel-body');
    if (body) body.scrollTop = 0;
  });

  /*
   * Back to level 1 on every open.
   *
   * The drawer keeps one cloned node and re-appends it (`mount()`), so an open
   * panel would still be open the next time the menu is opened. `mount()` runs
   * before `is-opened` lands on the drawer, so the content is in the document by
   * the time this fires.
   */
  if (typeof MutationObserver !== 'undefined') {
    var drawer = document.getElementById('DrawerMenu');

    if (drawer) {
      new MutationObserver(function () {
        if (!drawer.classList.contains('is-opened')) return;

        var open = drawer.querySelectorAll('.kik_mobile_menu_item.active');
        for (var i = 0; i < open.length; i += 1) {
          open[i].classList.remove('active');
        }

        var list = drawer.querySelector('.kik_mobile_menu');
        if (list) list.scrollTop = 0;
      }).observe(drawer, { attributes: true, attributeFilter: ['class'] });
    }
  }

  /* ------------------------------------------------------------------ *
   * Snowflake cursor trail (desktop, opt-in, respects reduced motion)
   * ------------------------------------------------------------------ */

  var reduceMotion =
    window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var isDesktop = function () {
    return window.matchMedia && window.matchMedia('(min-width: 1024px)').matches;
  };

  var lastSpawn = 0;
  var lastX = 0;
  var lastY = 0;

  function spawnBurst(x, y) {
    var now = performance.now();
    if (now - lastSpawn < 55) return;

    var dist = Math.hypot(x - lastX, y - lastY);
    if (dist < 10 && lastSpawn !== 0) return;

    lastSpawn = now;
    lastX = x;
    lastY = y;

    var count = 3 + Math.floor(Math.random() * 3);
    var maxRadius = 36; // keep the burst tight around the cursor

    for (var i = 0; i < count; i += 1) {
      var angle = Math.random() * Math.PI * 2;
      var spread = 6 + Math.random() * 18;
      var startX = x + Math.cos(angle) * spread;
      var startY = y + Math.sin(angle) * spread * 0.4;

      var flake = document.createElement('span');
      flake.className = 'sn-flake';
      flake.innerHTML = SNOWFLAKE_SVG;
      var size = 10 + Math.random() * 10;
      flake.style.width = size + 'px';
      flake.style.height = size + 'px';
      flake.style.color = Math.random() > 0.5 ? '#7ad4e8' : '#a8eef8';
      document.body.appendChild(flake);

      var fall = 10 + Math.random() * 22;
      var sway = (Math.random() - 0.5) * 20;
      var endX = startX + sway;
      var endY = startY + fall;

      var dx = endX - x;
      var dy = endY - y;
      var out = Math.hypot(dx, dy);
      if (out > maxRadius) {
        var scale = maxRadius / out;
        endX = x + dx * scale;
        endY = y + dy * scale;
      }
      if (endY < y - 8) endY = y - 8;
      if (endY > y + maxRadius) endY = y + maxRadius;

      var rot = 30 + Math.random() * 100;
      var duration = 1600 + Math.random() * 1200;
      var midX = startX + (endX - startX) * 0.45;
      var midY = startY + (endY - startY) * 0.5;

      var animation = flake.animate(
        [
          {
            transform: 'translate(' + startX + 'px, ' + startY + 'px) rotate(0deg) scale(.5)',
            opacity: 0,
          },
          {
            transform:
              'translate(' + startX + 'px, ' + startY + 'px) rotate(' + rot * 0.15 + 'deg) scale(1)',
            opacity: 0.72,
            offset: 0.1,
          },
          {
            transform:
              'translate(' + midX + 'px, ' + midY + 'px) rotate(' + rot * 0.55 + 'deg) scale(.95)',
            opacity: 0.5,
            offset: 0.55,
          },
          {
            transform: 'translate(' + endX + 'px, ' + endY + 'px) rotate(' + rot + 'deg) scale(.45)',
            opacity: 0,
          },
        ],
        { duration: duration, easing: 'cubic-bezier(.15,.65,.25,1)' }
      );

      animation.onfinish = (function (node) {
        return function () {
          node.remove();
        };
      })(flake);
    }
  }

  if (!reduceMotion && typeof Element.prototype.animate === 'function') {
    document.addEventListener(
      'mousemove',
      function (event) {
        if (!isDesktop()) return;
        var host = event.target.closest && event.target.closest('[data-sn-snowflake]');
        if (!host) {
          lastSpawn = 0;
          return;
        }
        spawnBurst(event.clientX, event.clientY);
      },
      { passive: true }
    );
  }
})();
